import { useState, useEffect, useCallback } from 'react';
import { Key, RefreshCw, Check, X, Clock, History, Eye, EyeOff, ChevronDown, ChevronRight, ChevronUp, AlertTriangle, RotateCcw, Cpu, Copy, ExternalLink, Zap, Shield, Edit2, Search } from 'lucide-react';
import { api } from '../api';

/* ── Collapse rapide pour sous-sections ── */
function ApiSubSection({ title, icon, badge, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        {icon && <span className="text-slate-400">{icon}</span>}
        <span className="text-sm font-semibold text-slate-700 flex-1">{title}</span>
        {badge !== undefined && badge > 0 && (
          <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full mr-1">{badge}</span>
        )}
        {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

const TYPE_LABELS = {
  attendance: 'Présence',
  payment: 'Paiement',
  private_class: 'Cours privé',
  personal_event: 'Évèn. personnel',
  festival: 'Festival',
};

const TYPE_COLORS = {
  attendance: 'bg-blue-50 text-blue-700 border-blue-200',
  payment: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  private_class: 'bg-purple-50 text-purple-700 border-purple-200',
  personal_event: 'bg-amber-50 text-amber-700 border-amber-200',
  festival: 'bg-rose-50 text-rose-700 border-rose-200',
};

function PayloadRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-slate-400 shrink-0 w-28">{label}</span>
      <span className="text-slate-700 font-medium break-all">{String(value)}</span>
    </div>
  );
}

function PayloadDisplay({ p, type }) {
  if (type === 'attendance') return (
    <div className="space-y-1">
      <PayloadRow label="Élève" value={p.student_name} />
      <PayloadRow label="Cours" value={p.course_name} />
      <PayloadRow label="Date" value={p.date} />
      <PayloadRow label="Séance ID" value={p.class_slot_id} />
      {!p.session_found && (
        <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
          <AlertTriangle size={12} /> Séance non trouvée — vérifiez la date
        </div>
      )}
    </div>
  );
  if (type === 'payment') return (
    <div className="space-y-1">
      <PayloadRow label="Élève" value={p.student_name} />
      <PayloadRow label="Montant" value={`${p.amount_chf} CHF`} />
      <PayloadRow label="Méthode" value={p.method} />
      <PayloadRow label="Date" value={p.date} />
      <PayloadRow label="Notes" value={p.notes} />
    </div>
  );
  if (type === 'private_class') return (
    <div className="space-y-1">
      <PayloadRow label="Élève" value={p.student_name} />
      <PayloadRow label="Début" value={p.datetime_start} />
      <PayloadRow label="Durée" value={`${p.duration_minutes} min`} />
      <PayloadRow label="Notes" value={p.notes} />
    </div>
  );
  if (type === 'personal_event') return (
    <div className="space-y-1">
      <PayloadRow label="Titre" value={p.title} />
      <PayloadRow label="Début" value={p.datetime_start} />
      <PayloadRow label="Fin" value={p.datetime_end} />
      <PayloadRow label="Notes" value={p.notes} />
    </div>
  );
  if (type === 'festival') return (
    <div className="space-y-1">
      <PayloadRow label="Nom" value={p.name} />
      <PayloadRow label="Début" value={p.date_start} />
      <PayloadRow label="Fin" value={p.date_end} />
      <PayloadRow label="Lieu" value={p.city} />
    </div>
  );
  return <pre className="text-xs text-slate-500">{JSON.stringify(p, null, 2)}</pre>;
}

// ── MCP Section ───────────────────────────────────────────────────────────────

function McpSection() {
  const [status, setStatus] = useState(null);
  const [newCreds, setNewCreds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState('');
  const [techOpen, setTechOpen] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);

  const backendUrl = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const mcpUrl     = `${backendUrl}/api/mcp/`;
  const discoveryUrl  = `${backendUrl}/.well-known/oauth-authorization-server`;
  const authorizeUrl  = `${backendUrl}/api/mcp/authorize`;
  const tokenUrl      = `${backendUrl}/api/mcp/token`;
  const registerUrl   = `${backendUrl}/api/mcp/register`;

  const load = useCallback(async () => {
    try { setStatus(await api.get('/api-admin/mcp-key')); }
    catch { setStatus({ configured: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!window.confirm('Générer une nouvelle clé d\'accès direct ? L\'ancienne sera invalide immédiatement.')) return;
    setLoading(true);
    try {
      const d = await api.post('/api-admin/mcp-key/generate');
      setNewCreds({ client_id: d.client_id, client_secret: d.mcp_key });
      setLegacyOpen(true);
      load();
    } catch (e) { alert('Erreur : ' + (e.message || e)); }
    finally { setLoading(false); }
  };

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const CopyBtn = ({ text, id, large = false }) => (
    <button
      onClick={() => copy(text, id)}
      className={`shrink-0 flex items-center gap-1 font-medium transition-colors rounded-lg
        ${large
          ? 'text-sm text-blue-700 hover:text-blue-900 px-3 py-1.5 hover:bg-blue-100 bg-blue-50 border border-blue-200'
          : 'text-xs text-slate-500 hover:text-slate-800 px-2 py-1 hover:bg-white'
        }`}
      data-testid={`copy-${id}-btn`}
    >
      {copied === id ? <Check size={large ? 14 : 12} className="text-emerald-500" /> : <Copy size={large ? 14 : 12} />}
      {copied === id ? 'Copié !' : 'Copier'}
    </button>
  );

  const UrlRow = ({ label, value, id }) => (
    <div>
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
        <code className="flex-1 text-xs font-mono text-slate-700 break-all">{value}</code>
        <CopyBtn text={value} id={id} />
      </div>
    </div>
  );

  const directUrl = newCreds ? `${mcpUrl}?key=${newCreds.client_secret}` : null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">

      {/* ── En-tête ── */}
      <div className="flex items-center gap-2">
        <Cpu size={16} className="text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-800">Connecteur MCP — Claude.ai</h3>
        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">
          OAuth 2.1 PKCE
        </span>
      </div>

      {/* ── URL principale — mise en évidence ── */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 space-y-3"
           data-testid="mcp-main-url-section">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-blue-600" />
          <p className="text-sm font-bold text-blue-900">URL à coller dans Claude.ai</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border-2 border-blue-300 px-3 py-2.5 shadow-sm">
          <code className="flex-1 text-sm font-mono font-semibold text-blue-900 break-all" data-testid="mcp-server-url">
            {mcpUrl}
          </code>
          <CopyBtn text={mcpUrl} id="mcp-url" large />
        </div>
        <div className="flex items-start gap-2 text-xs text-blue-700">
          <Shield size={12} className="mt-0.5 shrink-0 text-blue-500" />
          <span>
            Dans Claude.ai : <strong>Paramètres → Intégrations → Connecteurs MCP → Ajouter</strong>.
            L'authentification OAuth 2.1 se lance automatiquement — vous serez redirigé ici pour approuver l'accès.
          </span>
        </div>
      </div>

      {/* ── Détails OAuth 2.1 — accordéon ── */}
      <div className="border border-slate-100 rounded-xl overflow-hidden">
        <button
          onClick={() => setTechOpen(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="toggle-oauth-details"
        >
          <span>Détails techniques OAuth 2.1</span>
          {techOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </button>
        {techOpen && (
          <div className="px-4 pb-4 space-y-2.5 border-t border-slate-100 pt-3">
            <UrlRow label="Discovery (RFC 8414)" value={discoveryUrl} id="discovery-url" />
            <UrlRow label="Autorisation" value={authorizeUrl} id="authorize-url" />
            <UrlRow label="Token" value={tokenUrl} id="token-url" />
            <UrlRow label="Enregistrement client (RFC 7591)" value={registerUrl} id="register-url" />
          </div>
        )}
      </div>

      {/* ── Accès direct avec clé — fallback ── */}
      <div className="border border-slate-100 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setLegacyOpen(v => !v)}
            className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
            data-testid="toggle-legacy-key"
          >
            {legacyOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
            Accès direct avec clé (fallback sans OAuth)
          </button>
          <button
            onClick={generate}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs bg-slate-700 text-white px-3 py-1.5 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50"
            data-testid="generate-mcp-key-btn"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {status?.configured ? 'Régénérer' : 'Générer une clé'}
          </button>
        </div>

        {legacyOpen && (
          <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
            {!status?.configured && !newCreds && (
              <p className="text-xs text-slate-400 italic">
                Cliquez "Générer une clé" pour créer une URL d'accès direct (pour les clients ne supportant pas OAuth).
              </p>
            )}

            {newCreds && (
              <>
                <div>
                  <p className="text-xs text-slate-400 mb-1">URL directe <code className="bg-slate-100 rounded px-1">?key=</code></p>
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <code className="flex-1 text-xs font-mono text-amber-900 break-all">{directUrl}</code>
                    <CopyBtn text={directUrl} id="direct-url" />
                  </div>
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    Copiez maintenant — cette URL ne sera plus visible après rechargement.
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Client ID (legacy OAuth client_credentials)</p>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                    <code className="flex-1 text-xs font-mono text-slate-700 break-all">{newCreds.client_id}</code>
                    <CopyBtn text={newCreds.client_id} id="client-id" />
                  </div>
                </div>
              </>
            )}

            {status?.configured && !newCreds && (
              <div className="text-xs text-slate-500 space-y-0.5">
                <div>Clé créée le : <span className="text-slate-700">{new Date(status.created_at).toLocaleString('fr-CH')}</span></div>
                {status.last_used_at && (
                  <div>Dernière utilisation : <span className="text-slate-700">{new Date(status.last_used_at).toLocaleString('fr-CH')}</span></div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Outils disponibles ── */}
      <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
        <p className="text-xs font-semibold text-slate-600">11 outils disponibles pour Claude</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-slate-500">
          {[
            'lister_eleves', 'lister_cours_actifs', 'lister_rendez_vous',
            'lister_festivals', 'lister_taches',
            'enregistrer_presence', 'enregistrer_paiement', 'creer_cours_prive',
            'creer_evenement_personnel', 'creer_festival', 'creer_tache_google (immédiat)',
          ].map(e => (
            <code key={e} className="bg-white border border-slate-100 rounded px-1.5 py-0.5">{e}</code>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── Key Section ───────────────────────────────────────────────────────────────

function ApiKeySection() {
  const [status, setStatus] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get('/api-admin/key');
      setStatus(d);
    } catch { /* not configured */ setStatus({ configured: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!window.confirm('Générer une nouvelle clé ? L\'ancienne sera invalide immédiatement.')) return;
    setLoading(true);
    try {
      const d = await api.post('/api-admin/key/generate');
      setNewKey(d.api_key);
      setShowKey(true);
      load();
    } catch (e) {
      alert('Erreur : ' + (e.message || e));
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key size={16} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-800">Clé API externe</h3>
        </div>
        <button
          onClick={generate}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs bg-slate-800 text-white px-3 py-1.5 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
          data-testid="generate-api-key-btn"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          {status?.configured ? 'Régénérer' : 'Générer la clé'}
        </button>
      </div>

      {status?.configured && (
        <div className="text-xs text-slate-500 space-y-1">
          <div>Créée le : <span className="text-slate-700">{new Date(status.created_at).toLocaleString('fr-CH')}</span></div>
          {status.last_used_at && (
            <div>Dernière utilisation : <span className="text-slate-700">{new Date(status.last_used_at).toLocaleString('fr-CH')}</span></div>
          )}
        </div>
      )}

      {newKey && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
          <p className="text-xs font-semibold text-amber-800">Copiez cette clé maintenant — elle ne sera plus affichée :</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 font-mono break-all">
              {showKey ? newKey : '•'.repeat(newKey.length)}
            </code>
            <button onClick={() => setShowKey(v => !v)} className="p-1.5 text-amber-600 hover:text-amber-800">
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button
              onClick={() => { navigator.clipboard.writeText(newKey); }}
              className="text-xs bg-amber-600 text-white px-2 py-1.5 rounded-lg hover:bg-amber-700"
            >
              Copier
            </button>
          </div>
          <p className="text-xs text-amber-600">Header HTTP requis : <code className="bg-white px-1 rounded">X-API-Key: &lt;votre_clé&gt;</code></p>
        </div>
      )}

      <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
        <p className="text-xs font-semibold text-slate-600">Endpoints disponibles</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-slate-500">
          {[
            'GET /api/ext/students',
            'GET /api/ext/courses',
            'GET /api/ext/private-classes',
            'GET /api/ext/festivals',
            'GET /api/ext/tasks',
            'POST /api/ext/attendance',
            'POST /api/ext/payment',
            'POST /api/ext/private-class',
            'POST /api/ext/personal-event',
            'POST /api/ext/festival',
            'POST /api/ext/task (immédiat)',
          ].map(e => (
            <code key={e} className="bg-white border border-slate-100 rounded px-1.5 py-0.5">{e}</code>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Pending Actions ───────────────────────────────────────────────────────────

/** Modale d'édition générique d'une action en attente */
function EditActionModal({ action, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [form, setForm] = useState(() => {
    const p = { ...action.payload };
    // Séparer datetime_start en date + time pour les cours privés
    if (p.datetime_start) {
      p._date = p.datetime_start.slice(0, 10);
      p._time = p.datetime_start.slice(11, 16);
    }
    return p;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/students').then(setStudents).catch(() => {});
  }, []);

  const matchStudent = (s, q) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase().trim();
    const ph = (s.phone || '').replace(/\D/g, '');
    const st = t.replace(/\D/g, '');
    return `${s.first_name} ${s.last_name}`.toLowerCase().includes(t) ||
      (s.instagram || '').toLowerCase().includes(t) ||
      (st.length >= 4 && ph.includes(st));
  };

  const filteredStudents = students.filter(s => matchStudent(s, studentSearch));

  const selectStudent = (s) => {
    setForm(f => ({
      ...f,
      student_id: s.id,
      student_name: `${s.first_name} ${s.last_name}`,
    }));
    setStudentSearch('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Reconstruire datetime_start depuis date + time
      if (payload._date && payload._time) {
        payload.datetime_start = `${payload._date}T${payload._time}`;
      }
      delete payload._date;
      delete payload._time;
      await api.put(`/api-admin/actions/${action.id}`, { payload });
      onSaved();
      onClose();
    } catch (e) {
      alert('Erreur : ' + (e.detail || e.message || JSON.stringify(e)));
    } finally { setSaving(false); }
  };

  const needsStudent = ['attendance', 'payment', 'private_class'].includes(action.type);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">Modifier l'action</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">

          {/* Sélecteur d'élève */}
          {needsStudent && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Élève</label>
              {form.student_name && (
                <div className="flex items-center justify-between bg-[#E0F7FA] text-[#0288D1] rounded-lg px-3 py-2 text-sm font-semibold mb-2">
                  <span>{form.student_name}</span>
                  <button onClick={() => setForm(f => ({ ...f, student_id: '', student_name: '' }))} className="text-[#0288D1]/60 hover:text-[#0288D1]"><X size={14} /></button>
                </div>
              )}
              <div className="relative mb-1">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  placeholder="Nom, Instagram, téléphone…"
                  className="w-full h-9 pl-8 pr-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]"
                />
              </div>
              {studentSearch.trim() && (
                <div className="max-h-36 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
                  {filteredStudents.slice(0, 8).map(s => (
                    <button key={s.id} onClick={() => selectStudent(s)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#E0F7FA] transition-colors">
                      {s.first_name} {s.last_name}
                      {s.instagram && <span className="text-slate-400 text-xs ml-2">@{s.instagram}</span>}
                    </button>
                  ))}
                  {filteredStudents.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">Aucun élève trouvé</p>}
                </div>
              )}
            </div>
          )}

          {/* Champs spécifiques par type */}
          {action.type === 'private_class' && (<>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Date</label>
                <input type="date" value={form._date || ''} onChange={e => setForm(f => ({ ...f, _date: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Heure</label>
                <input type="time" value={form._time || ''} onChange={e => setForm(f => ({ ...f, _time: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Durée (min)</label>
              <input type="number" value={form.duration_minutes || 60} min={15} max={480} step={15}
                onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 60 }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-[#0288D1]" />
            </div>
          </>)}

          {action.type === 'payment' && (<>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Montant CHF</label>
                <input type="number" value={form.amount_chf || ''} min={0} step={1}
                  onChange={e => setForm(f => ({ ...f, amount_chf: parseFloat(e.target.value) || 0 }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Méthode</label>
                <select value={form.method || 'cash'} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]">
                  {['cash', 'twint', 'virement', 'autre'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Date</label>
              <input type="date" value={form.date || form._date || ''} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-[#0288D1]" />
            </div>
          </>)}

          {action.type === 'personal_event' && (<>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Titre</label>
              <input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Date</label>
                <input type="date" value={form._date || ''} onChange={e => setForm(f => ({ ...f, _date: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Heure</label>
                <input type="time" value={form._time || ''} onChange={e => setForm(f => ({ ...f, _time: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-[#0288D1]" />
            </div>
          </>)}

          {action.type === 'festival' && (<>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Nom du festival</label>
              <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Début</label>
                <input type="date" value={form.date_start || ''} onChange={e => setForm(f => ({ ...f, date_start: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Fin</label>
                <input type="date" value={form.date_end || ''} onChange={e => setForm(f => ({ ...f, date_end: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Ville</label>
              <input value={form.city || ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#0288D1]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-[#0288D1]" />
            </div>
          </>)}
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Annuler</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#0288D1] text-white text-sm font-semibold hover:bg-[#0277BD] transition-colors disabled:opacity-60">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PendingActions({ onUpdate }) {
  const [actions, setActions] = useState([]);
  const [loadingId, setLoadingId] = useState('');
  const [editAction, setEditAction] = useState(null);

  const load = useCallback(async () => {
    try { setActions(await api.get('/api-admin/pending')); }
    catch { setActions([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = async (id) => {
    setLoadingId(id + '_confirm');
    try {
      await api.post(`/api-admin/actions/${id}/confirm`);
      await load();
      onUpdate();
    } catch (e) {
      alert('Erreur : ' + (e.detail || e.message || JSON.stringify(e)));
    } finally { setLoadingId(''); }
  };

  const reject = async (id) => {
    if (!window.confirm('Rejeter cette action ?')) return;
    setLoadingId(id + '_reject');
    try {
      await api.post(`/api-admin/actions/${id}/reject`);
      await load();
    } catch (e) { alert(e.detail || e.message); }
    finally { setLoadingId(''); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  if (actions.length === 0) return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center text-slate-400 text-sm">
      <Clock size={24} className="mx-auto mb-2 opacity-40" />
      Aucune action en attente
    </div>
  );

  return (
    <>
      {editAction && (
        <EditActionModal
          action={editAction}
          onClose={() => setEditAction(null)}
          onSaved={load}
        />
      )}
      <div className="space-y-2">
        {actions.map(a => (
          <div key={a.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`pending-action-${a.type}`}>
            <div className="flex items-start gap-3">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border shrink-0 mt-0.5 ${TYPE_COLORS[a.type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                {TYPE_LABELS[a.type] || a.type}
              </span>
              <div className="flex-1 min-w-0">
                <PayloadDisplay p={a.payload} type={a.type} />
                <p className="text-xs text-slate-300 mt-2">{fmt(a.created_at)}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  onClick={() => setEditAction(a)}
                  className="flex items-center gap-1 text-xs bg-slate-50 text-slate-500 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200"
                  data-testid={`edit-action-${a.id}`}
                  title="Modifier"
                >
                  <Edit2 size={12} />
                </button>
                <button
                  onClick={() => confirm(a.id)}
                  disabled={loadingId === a.id + '_confirm'}
                  className="flex items-center gap-1 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  data-testid={`confirm-action-${a.id}`}
                >
                  <Check size={12} />
                  {loadingId === a.id + '_confirm' ? '...' : 'Confirmer'}
                </button>
                <button
                  onClick={() => reject(a.id)}
                  disabled={loadingId === a.id + '_reject'}
                  className="flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                  data-testid={`reject-action-${a.id}`}
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── History ───────────────────────────────────────────────────────────────────

function ActionHistory() {
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState('');

  const load = useCallback(async () => {
    try { setLogs(await api.get('/api-admin/history')); }
    catch { setLogs([]); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const cancel = async (id) => {
    if (!window.confirm('Annuler ce paiement ? Le crédit sera supprimé du compte élève.')) return;
    setCancellingId(id);
    try {
      await api.post(`/api-admin/history/${id}/cancel`);
      await load();
    } catch (e) { alert(e.detail || e.message); }
    finally { setCancellingId(''); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 rounded-2xl transition-colors"
        data-testid="toggle-api-history"
      >
        <div className="flex items-center gap-2">
          <History size={15} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-700">Historique des actions validées</span>
        </div>
        {open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          {logs.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">Aucune action validée pour l'instant</p>
          )}
          {logs.map(l => (
            <div key={l.id} className={`flex items-start gap-3 p-3 rounded-xl border text-xs ${l.cancelled ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-white border-slate-100'}`}>
              <span className={`shrink-0 px-2 py-0.5 rounded-full border font-semibold ${TYPE_COLORS[l.type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                {TYPE_LABELS[l.type] || l.type}
              </span>
              <div className="flex-1 min-w-0">
                <PayloadDisplay p={l.payload} type={l.type} />
                <p className="text-slate-300 mt-1">
                  {fmt(l.confirmed_at)} — par {l.confirmed_by}
                  {l.cancelled && <span className="ml-2 text-red-400 font-medium">• Annulé le {fmt(l.cancelled_at)}</span>}
                </p>
              </div>
              {l.can_cancel && !l.cancelled && (
                <button
                  onClick={() => cancel(l.id)}
                  disabled={cancellingId === l.id}
                  className="shrink-0 flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-1 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                  data-testid={`cancel-payment-${l.id}`}
                >
                  <RotateCcw size={11} />
                  Annuler
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function ApiAdminPanel() {
  const [pendingCount, setPendingCount] = useState(0);

  const refreshCount = useCallback(async () => {
    try {
      const data = await api.get('/api-admin/pending');
      setPendingCount(data.length);
    } catch { /* not admin or not configured */ }
  }, []);

  useEffect(() => { refreshCount(); }, [refreshCount]);

  return (
    <div className="space-y-3">
      <ApiSubSection title="Connecteur MCP (Claude.ai)" icon={<Cpu size={14} />} defaultOpen={true}>
        <McpSection />
      </ApiSubSection>
      <ApiSubSection title="Clé API externe" icon={<Key size={14} />} defaultOpen={false}>
        <ApiKeySection />
      </ApiSubSection>
      <ApiSubSection title="Actions en attente" icon={<Clock size={14} />} badge={pendingCount} defaultOpen={pendingCount > 0}>
        <PendingActions onUpdate={refreshCount} />
      </ApiSubSection>
      <ApiSubSection title="Historique des actions" icon={<History size={14} />} defaultOpen={false}>
        <ActionHistory />
      </ApiSubSection>
    </div>
  );
}
